import { z } from 'zod'
import type { ItemOccasion } from './enrichment'

/** The occasions people can mark a place as good for, in the order the place page shows them. */
export const PLACE_GOOD_FOR_OCCASIONS = [
  'date',
  'business',
  'study',
  'family',
  'friends',
  'party',
  'coffee',
] as const satisfies readonly ItemOccasion[]

export const placeGoodForOccasionSchema = z.enum(PLACE_GOOD_FOR_OCCASIONS)
export type PlaceGoodForOccasion = z.infer<typeof placeGoodForOccasionSchema>

export function isPlaceGoodForOccasion(value: string): value is PlaceGoodForOccasion {
  return PLACE_GOOD_FOR_OCCASIONS.some((occasion) => occasion === value)
}

/** How many occasions a place may name for itself as a hint for the sorting. */
export const MAX_OWNER_GOOD_FOR = 3

/** A place's published labels and the caller's own marks on it. */
export const listingGoodForSchema = z.object({
  /** Occasions enough eligible people agree on; never with counts or names. */
  labels: z.array(placeGoodForOccasionSchema),
  mine: z.array(placeGoodForOccasionSchema),
})
export type ListingGoodFor = z.infer<typeof listingGoodForSchema>

/** What a place says it is good for, and what its guests say. */
export const listingOwnerGoodForSchema = z.object({
  chosen: z.array(placeGoodForOccasionSchema),
  /** Only published labels, with how many eligible people agree. */
  guests: z.array(
    z.object({ occasion: placeGoodForOccasionSchema, voters: z.number().int().positive() }),
  ),
})
export type ListingOwnerGoodFor = z.infer<typeof listingOwnerGoodForSchema>

export const updateListingOwnerGoodForRequestSchema = z.object({
  chosen: z
    .array(placeGoodForOccasionSchema)
    .max(MAX_OWNER_GOOD_FOR)
    .refine((occasions) => new Set(occasions).size === occasions.length, {
      message: 'Each occasion only once',
    }),
})
export type UpdateListingOwnerGoodForRequest = z.infer<
  typeof updateListingOwnerGoodForRequestSchema
>
